import { useId } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { useFormikContext } from "formik";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useScope } from "@state/scopeContext.lib";
import { TaskCreate } from "@common/contracts/task.contract";
import { TaskType } from "@common/types/task";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { useWorkspaceCloses } from "@api/close.api";
import { TaskPrioritySelect } from "@components/tasks/TaskPrioritySelect/TaskPrioritySelect";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { DetailsAccordionItem } from "@design-system/Accordion/DetailsAccordionItem";
import { Button } from "@design-system/Button/Button";
import { TimePicker } from "@design-system/TimePicker/TimePicker";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { VStack } from "@design-system/VStack/VStack";
import classNames from "classnames";
import styles from "./CreateTaskDrawer.module.scss";
import fieldStyles from "./TaskDrawerFields.module.scss";
import { NullableSelectField } from "./NullableSelectField";
import { TaskAssigneeSelect } from "./TaskAssigneeSelect";
import { TaskGroupSelect } from "./TaskGroupSelect";
import { TaskPeriodFields } from "./TaskPeriodFields";
import { TaskRelationField } from "./TaskRelationField";
import { TaskSplitField } from "./TaskSplitField";
import { TaskStatementFields } from "./TaskStatementFields";
import { hasTaskRelations } from "./taskSplit";
import { TaskWorkflowField } from "./TaskWorkflowField";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";

const fields = createFormikFields<TaskCreate>();

const DETAILS_SECTION = "details";
const WORKFLOW_SECTION = "workflow";

interface TaskCreationFormDetailsProps {
    hideEntitySelection?: boolean;
    hideGroupSelection?: boolean;
    hideRelations?: boolean;
    showWorkflowPicker?: boolean;
    splitOptions?: { transactionSplitBlocker: string | null };
    isWorkflowLocked?: boolean;
    onTemplateApplied?: () => void;
    onWorkflowChange?: (type: TaskType) => void;
}

export const TaskCreationFormDetails = ({
    hideEntitySelection,
    hideGroupSelection,
    hideRelations,
    showWorkflowPicker,
    splitOptions,
    isWorkflowLocked,
    onTemplateApplied,
    onWorkflowChange,
}: TaskCreationFormDetailsProps) => {
    const dueDateLabelId = useId();
    const entitiesLabelId = useId();
    const formik = useFormikContext<TaskCreate>();
    const { organizationId, workspaceId } = useScope();
    const sortedCloseStages = useSortedCloseStages();
    const { data: closes = [] } = useWorkspaceCloses();

    const isCloseStageVisible =
        !!organizationId &&
        sortedCloseStages.length > 0 &&
        !!formik.values.closeId;
    const isCloseVisible = !!organizationId && closes.length > 0;
    const isRelationsVisible = !!workspaceId && !hideRelations;
    const isDroppedRelationsHintVisible =
        !!workspaceId && !!hideRelations && hasTaskRelations(formik.values);

    const defaultOpenSections = [
        DETAILS_SECTION,
        ...(showWorkflowPicker ? [WORKFLOW_SECTION] : []),
    ];

    return (
        <AccordionRoot
            multiple
            defaultValue={defaultOpenSections}
            className={classNames(
                fieldStyles.taskDrawerAccordion,
                styles.detailsAccordion,
            )}
        >
            <DetailsAccordionItem value={DETAILS_SECTION} label="Details">
                <VStack gap={4}>
                    <DescriptionListItem
                        label={<span id={dueDateLabelId}>Due date</span>}
                    >
                        <div className={styles.datePickerContainer}>
                            <fields.Date
                                name="dueDate"
                                aria-labelledby={dueDateLabelId}
                                className={styles.detailField}
                                variant="panel-input"
                                clearable
                                emptyValue={null}
                                showTriggerIcon={false}
                                placeholder="Set due date"
                                buttonProps={{
                                    className: classNames({
                                        [styles.emptyValue]:
                                            !formik.values.dueDate,
                                    }),
                                }}
                            />
                        </div>
                    </DescriptionListItem>
                    <DescriptionListItem label="Priority">
                        <div className={fieldStyles.detailSelectWrapper}>
                            <TaskPrioritySelect
                                value={formik.values.priority ?? null}
                                onValueChange={(value) =>
                                    formik.setFieldValue("priority", value)
                                }
                                buttonProps={{
                                    variant: "primary",
                                    block: false,
                                    className: classNames(
                                        fieldStyles.detailSelectTrigger,
                                        {
                                            [fieldStyles.emptyValue]:
                                                !formik.values.priority,
                                        },
                                    ),
                                }}
                            />
                        </div>
                    </DescriptionListItem>
                    <DescriptionListItem label="Estimated time">
                        <div className={fieldStyles.detailSelectWrapper}>
                            <TimePicker
                                hideIcon
                                value={formik.values.estimatedTime ?? undefined}
                                onChange={(value) =>
                                    formik.setFieldValue(
                                        "estimatedTime",
                                        value ?? null,
                                    )
                                }
                                trigger={
                                    <Button
                                        type="button"
                                        variant="primary"
                                        size="md"
                                        block={false}
                                        className={classNames(
                                            fieldStyles.detailSelectTrigger,
                                            fieldStyles.justifyLeft,
                                            {
                                                [fieldStyles.emptyValue]:
                                                    !formik.values
                                                        .estimatedTime,
                                            },
                                        )}
                                    >
                                        {formatDuration(
                                            formik.values.estimatedTime ??
                                                undefined,
                                        ) || "Set estimate"}
                                    </Button>
                                }
                            />
                        </div>
                    </DescriptionListItem>
                    {!hideEntitySelection && (
                        <DescriptionListItem
                            label={<span id={entitiesLabelId}>Entities</span>}
                        >
                            <div className={fieldStyles.detailSelectWrapper}>
                                <fields.Entity
                                    name="entityIds"
                                    aria-labelledby={entitiesLabelId}
                                    className={styles.detailField}
                                    renderTrigger={({
                                        triggerProps,
                                        selectedEntities,
                                    }) => (
                                        <Button
                                            {...triggerProps}
                                            type="button"
                                            variant="primary"
                                            size="md"
                                            className={classNames(
                                                fieldStyles.detailSelectTrigger,
                                                fieldStyles.justifyLeft,
                                                triggerProps.className,
                                                {
                                                    [fieldStyles.emptyValue]:
                                                        !selectedEntities.length,
                                                },
                                            )}
                                        >
                                            {selectedEntities.length ? (
                                                <EntityAvatarGroup
                                                    entities={selectedEntities}
                                                    max={3}
                                                    size="xs"
                                                    label={pluralizeWithCount(
                                                        "Entity",
                                                        selectedEntities.length,
                                                    )}
                                                />
                                            ) : (
                                                "Add entity"
                                            )}
                                        </Button>
                                    )}
                                />
                            </div>
                        </DescriptionListItem>
                    )}
                    <DescriptionListItem label="Assignee">
                        <div className={fieldStyles.detailSelectWrapper}>
                            <TaskAssigneeSelect
                                value={formik.values.assigneeId}
                                onChange={(id) =>
                                    formik.setFieldValue("assigneeId", id)
                                }
                                buttonProps={{
                                    variant: "primary",
                                    block: false,
                                    className: classNames(
                                        fieldStyles.detailSelectTrigger,
                                        {
                                            [fieldStyles.emptyValue]:
                                                !formik.values.assigneeId,
                                        },
                                    ),
                                }}
                            />
                        </div>
                    </DescriptionListItem>
                    {!hideGroupSelection && (
                        <DescriptionListItem label="Task group">
                            <div className={fieldStyles.detailSelectWrapper}>
                                <TaskGroupSelect
                                    value={formik.values.groupId}
                                    onChange={(id) =>
                                        formik.setFieldValue("groupId", id)
                                    }
                                    buttonProps={{
                                        variant: "primary",
                                        block: false,
                                        className: classNames(
                                            fieldStyles.detailSelectTrigger,
                                            {
                                                [fieldStyles.emptyValue]:
                                                    !formik.values.groupId,
                                            },
                                        ),
                                    }}
                                />
                            </div>
                        </DescriptionListItem>
                    )}
                    {isCloseVisible && (
                        <NullableSelectField
                            label="Service"
                            placeholder="Set service"
                            noneLabel="No service"
                            searchPlaceholder="Search services…"
                            emptyLabel="No matching services"
                            items={closes}
                            value={formik.values.closeId}
                            onValueChange={(value) => {
                                formik.setFieldValue("closeId", value);
                                if (!value) {
                                    formik.setFieldValue("closeStageId", null);
                                }
                            }}
                        />
                    )}
                    {isCloseStageVisible && (
                        <NullableSelectField
                            label="Stage"
                            placeholder="Set stage"
                            noneLabel="No stage"
                            searchPlaceholder="Search stages…"
                            emptyLabel="No matching stages"
                            items={sortedCloseStages}
                            value={formik.values.closeStageId}
                            onValueChange={(value) =>
                                formik.setFieldValue("closeStageId", value)
                            }
                        />
                    )}
                    {isRelationsVisible && (
                        <>
                            <TaskRelationField
                                name="blockedByTaskIds"
                                label="Blocked by"
                            />
                            <TaskRelationField
                                name="blockingTaskIds"
                                label="Blocking"
                            />
                            <TaskRelationField
                                name="relatedTaskIds"
                                label="Related tasks"
                            />
                        </>
                    )}
                    {isDroppedRelationsHintVisible && (
                        <p className={fieldStyles.relationsHint}>
                            Task relations aren't kept when splitting on
                            transaction
                        </p>
                    )}
                </VStack>
            </DetailsAccordionItem>
            {showWorkflowPicker && (
                <DetailsAccordionItem value={WORKFLOW_SECTION} label="Action">
                    <VStack gap={4}>
                        <DescriptionListItem label="Action">
                            <div className={fieldStyles.detailSelectWrapper}>
                                <TaskWorkflowField
                                    isLocked={isWorkflowLocked}
                                    onTemplateApplied={onTemplateApplied}
                                    onWorkflowChange={onWorkflowChange}
                                    triggerClassName={
                                        fieldStyles.detailSelectTrigger
                                    }
                                />
                            </div>
                        </DescriptionListItem>
                        {splitOptions && (
                            <TaskSplitField
                                transactionSplitBlocker={
                                    splitOptions.transactionSplitBlocker
                                }
                            />
                        )}
                        <TaskPeriodFields />
                        <TaskStatementFields />
                    </VStack>
                </DetailsAccordionItem>
            )}
        </AccordionRoot>
    );
};
